import * as _ from 'underscore';

import * as React from 'react';
import { mouseIsInsideElement, mouseIsOutsideElement } from '../../utils/RectangleUtils';

export const DEFAULT_TOOLTIP_DELAY = 700;
export const MAX_MOUSEMOVE_EVENTS_PER_SECOND = 10;
export const MOUSEMOVE_THROTTLE_INTERVAL = 1000 / MAX_MOUSEMOVE_EVENTS_PER_SECOND;

export type HoverDelayType = number | 'none';

export interface HoverProps {
  /**
   * Groups ensure that only one element per group is hovered at a time. Before entering,
   * we first leave the last item in this group entered.
   */
  readonly groupName: string;
  readonly onEnter: () => void;
  readonly onLeave: () => void;
  readonly enterDelay?: HoverDelayType;
  readonly leaveOnScroll?: boolean;
  readonly className?: string;
  readonly useDocument?: boolean;
}

const HoverDictionary: {[testId: string]: Hover} = {};

export class Hover extends React.Component<HoverProps> {
  private containerElement: HTMLElement;
  private scrollContainerElement: Element;
  private isMouseOverTarget = false;
  private isWatchingMovement = false;

  private get enterDelay(): HoverDelayType {
    return this.props.enterDelay !== undefined && this.props.enterDelay !== null ? this.props.enterDelay : 'none';
  }

  public render() {
    return (
      <div ref={this.setContainerElementRef} data-tb-test-id={`${this.props.groupName}-hover`} onMouseEnter={this.handleMouseEnter} className = {this.props.className}>
        {this.props.children}
      </div>
    );
  }

  private setContainerElementRef = (el: HTMLElement) => {
    this.containerElement = el;
  }

  public componentWillUnmount() {
    this.containerElement = null;
    this.stopWatchingMovement();
  }

  private enter = () => {
    if (this.containerElement) {
      const previousHoverEntered = HoverDictionary[this.props.groupName];
      if (previousHoverEntered && previousHoverEntered !== this) {
        previousHoverEntered.leave();
      }

      this.containerElement && this.props.onEnter();
      HoverDictionary[this.props.groupName] = this;
    }
  }

  private leave = () => {
    this.containerElement && this.props.onLeave();
  }

  //
  // TODO: How closely does this logic mirror the ChromeTooltip?
  //

  private handleMouseEnter = () => {
    this.isMouseOverTarget = true;

    if (this.enterDelay !== 'none') {
      setTimeout(() => {
        if (this.isMouseOverTarget) {
          this.enter();
        }
      }, this.enterDelay);
    } else {
      this.enter();
    }
    this.startWatchingMovement();
  }

  private handleMouseMove = (evt: MouseEvent) => {
    if (this.containerElement) {
      this.isMouseOverTarget = mouseIsInsideElement(evt, this.containerElement);
      if (!this.isMouseOverTarget) {
        this.leave();
        this.stopWatchingMovement();
      }
    } else {
      this.stopWatchingMovement();
    }
  }

  private handleScroll = () => {
    this.leave();
    this.stopWatchingMovement();
  }

  private throttledHandleMouseMove = _.throttle(this.handleMouseMove, MOUSEMOVE_THROTTLE_INTERVAL);

  private startWatchingMovement = () => {
    if (!this.isWatchingMovement) {
      if (this.props.leaveOnScroll) {
        this.scrollContainerElement = findScrollContainer(this.containerElement);
        if (this.scrollContainerElement) {
          this.scrollContainerElement.addEventListener('scroll', this.handleScroll);
        }
      }

      if (this.props.useDocument) {
        document.addEventListener('mousemove', this.throttledHandleMouseMove);
      } else {
        window.addEventListener('mousemove', this.throttledHandleMouseMove);
      }
      this.isWatchingMovement = true;
    }
  }

  private stopWatchingMovement = () => {
    if (this.props.useDocument) {
      document.removeEventListener('mousemove', this.throttledHandleMouseMove);
    } else {
      window.removeEventListener('mousemove', this.throttledHandleMouseMove);
    }
    if (this.scrollContainerElement) {
      this.scrollContainerElement.removeEventListener('scroll', this.handleScroll);
    }
    this.isWatchingMovement = false;
  }
}

/**
 * Finds the first parent of the specified element that scrolls vertically.
 *
 * @returns An element that scrolls vertically, or null if none is found.
 */
function findScrollContainer(node: Element): Element {
  if (!node) {
    return null;
  }

  if (node.clientHeight > 0 && node.scrollHeight > node.clientHeight) {
    return node;
  } else {
    return findScrollContainer(node.parentElement);
  }
}
